import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, Sparkles, Scissors, Palette, Crown, Star, Quote, Instagram, MapPin, Plus } from "lucide-react";
import Reveal from "@/components/Reveal";
import TiltCard from "@/components/TiltCard";
import GoogleReviewButton from "@/components/GoogleReviewButton";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import {
  MIRROR_CLIENT,
  SALON_INTERIOR_COTRIL,
  HOME_HERO,
  galleryItems,
} from "@/data/gallery";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (n) => new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(n);

// 4 hand-picked product IDs to feature on home
const FEATURED_PRODUCT_IDS = [
  "cotril-hydra-mask-200",
  "cotril-nutro-miracle-oil-100",
  "cotril-k-smooth-serum-50",
  "cotril-firewall-spray-250",
];

// 6 instagram-style preview shots
const IG_PREVIEW = galleryItems.filter((g) =>
  ["g47", "g14", "g4", "g42", "g39", "g38"].includes(g.id)
).slice(0, 6);

const featured = [
  {
    icon: Palette,
    title: "Balayage",
    desc: "Técnica artesanal de reflexos pintados à mão para um efeito sol-na-pele natural.",
    price: "desde 100€",
  },
  {
    icon: Sparkles,
    title: "Morena Iluminada",
    desc: "Iluminação subtil para morenas, com profundidade e brilho luxuoso.",
    price: "desde 100€",
  },
  {
    icon: Crown,
    title: "Botox Capilar",
    desc: "Reconstrução intensiva — disciplina, brilho e suavidade extremos.",
    price: "desde 50€",
  },
  {
    icon: Scissors,
    title: "Penteados",
    desc: "Penteados cuidados para o seu dia especial. Noivas, eventos, cerimónias.",
    price: "desde 35€",
  },
];

const testimonials = [
  {
    name: "Catarina M.",
    text: "A Tamara transformou completamente o meu cabelo. O balayage ficou exatamente como sonhei — natural, luminoso, sem agressão.",
    rating: 5,
  },
  {
    name: "Inês P.",
    text: "Profissionalismo do início ao fim. Salão acolhedor, produtos de altíssima qualidade. Já não vou a mais lado nenhum.",
    rating: 5,
  },
  {
    name: "Rita S.",
    text: "Sou cliente há dois anos. A Tamara percebe perfeitamente o que cada pessoa precisa. Resultados sempre impecáveis.",
    rating: 5,
  },
];

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const { addItem } = useCart();

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((res) => {
        const map = new Map(res.data.map((p) => [p.id, p]));
        setFeaturedProducts(FEATURED_PRODUCT_IDS.map((id) => map.get(id)).filter(Boolean));
      })
      .catch(() => {});
  }, []);

  const handleAdd = (p) => {
    addItem(p, 1);
    toast.success(`${p.name} adicionado ao carrinho`);
  };

  return (
    <div data-testid="home-page" className="overflow-x-clip">
      {/* ========== CINEMATIC HERO ========== */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        {/* Background asymmetric image */}
        <div className="absolute inset-0 lg:left-auto lg:w-[60%] lg:right-0 perspective-1000">
          <div className="w-full h-full ken-burns">
            <img
              src={HOME_HERO}
              alt="Tamara Alves Hairstylist"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-beige via-beige/70 to-transparent lg:bg-gradient-to-r lg:from-beige lg:via-beige/40 lg:to-transparent" />
        </div>

        {/* Giant decorative A */}
        <span
          aria-hidden
          className="hidden lg:block absolute -bottom-32 -left-20 font-serif italic text-[36rem] leading-none text-gold/[0.07] select-none pointer-events-none"
        >
          A
        </span>

        <div className="container-luxe relative z-10 grid lg:grid-cols-2 gap-8">
          <div className="max-w-xl">
            <Reveal>
              <p className="overline mb-6" data-testid="hero-overline">— Hairstylist · Venda do Pinheiro</p>
              <h1 className="font-serif text-6xl sm:text-7xl lg:text-8xl font-light leading-[0.95] text-ink">
                A arte do{" "}
                <span className="italic gold-text">Balayage</span>
                <br />
                e da cor.
              </h1>
              <p className="mt-8 text-ink/70 text-lg max-w-md leading-relaxed">
                Especialista em iluminação capilar, formada pela Academia Lucia Piloto.
                Resultados naturais, luminosos e duradouros — feitos exclusivamente para si.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link to="/agendamentos" data-testid="hero-book-btn" className="btn-gold group">
                  Marcar Sessão
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/loja" data-testid="hero-shop-btn" className="btn-outline-gold">
                  Pre-Shop Cotril
                </Link>
              </div>

              <div className="mt-12 flex flex-wrap gap-6 items-center">
                <div>
                  <div className="flex gap-0.5 text-gold">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-current" />
                    ))}
                  </div>
                  <p className="text-xs text-ink/60 mt-1">4.9 / 5 no Google · 4.976 avaliações</p>
                </div>
                <GoogleReviewButton variant="button" />
              </div>
            </Reveal>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-ink/40 z-10">
          <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
          <span className="w-px h-12 bg-gradient-to-b from-gold to-transparent animate-pulse" />
        </div>
      </section>

      {/* ========== MARQUEE STRIP ========== */}
      <section className="border-y border-gold/20 bg-ink text-beige overflow-hidden">
        <div className="marquee py-6">
          {[0, 1].map((k) => (
            <div key={k} className="marquee__track items-center">
              {Array.from({ length: 6 }).map((_, i) => (
                <span key={i} className="flex items-center gap-12 text-2xl font-serif italic text-beige/80 whitespace-nowrap">
                  <span className="gold-text">Balayage</span>
                  <span className="w-1 h-1 rounded-full bg-gold" />
                  <span>Morena Iluminada</span>
                  <span className="w-1 h-1 rounded-full bg-gold" />
                  <span className="gold-text">Cotril Professional</span>
                  <span className="w-1 h-1 rounded-full bg-gold" />
                  <span>Academia Lucia Piloto</span>
                  <span className="w-1 h-1 rounded-full bg-gold" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ========== SERVICES ========== */}
      <section className="container-luxe py-24 sm:py-32" data-testid="home-services">
        <Reveal className="grid lg:grid-cols-12 gap-8 lg:gap-16 mb-16">
          <div className="lg:col-span-5">
            <p className="overline mb-4">— Especialidades</p>
            <h2 className="font-serif text-5xl lg:text-6xl font-light leading-tight text-ink">
              Cada cabelo,<br />
              uma <span className="italic gold-text">obra de arte</span>.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="text-ink/70 leading-relaxed text-lg">
              Trabalho artesanal, técnico e atento ao detalhe — desde a iluminação subtil
              à transformação total. Cada serviço é estudado para realçar a sua
              personalidade e a saúde do seu cabelo.
            </p>
          </div>
        </Reveal>

        <Reveal stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.map((s, idx) => {
            const Icon = s.icon;
            return (
              <TiltCard
                key={s.title}
                maxTilt={7}
                className="bg-beige-light p-8 group relative cursor-pointer"
              >
                <div data-testid={`featured-service-${idx}`}>
                  <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-700" />
                  <div className="w-14 h-14 border border-gold/40 flex items-center justify-center mb-6 group-hover:bg-gold-bright group-hover:border-gold transition-all duration-500">
                    <Icon className="w-6 h-6 text-gold group-hover:text-ink transition-colors" strokeWidth={1.25} />
                  </div>
                  <h3 className="font-serif text-2xl text-ink mb-3">{s.title}</h3>
                  <p className="text-sm text-ink/65 leading-relaxed mb-6">{s.desc}</p>
                  <p className="overline text-gold-dark">{s.price}</p>
                </div>
              </TiltCard>
            );
          })}
        </Reveal>

        <Reveal className="mt-16 text-center">
          <Link to="/servicos" data-testid="home-all-services-link" className="btn-outline-gold">
            Ver todos os serviços <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </section>

      {/* ========== ABOUT TEASER with new salon photos ========== */}
      <section className="bg-beige-light border-y border-gold/10">
        <div className="container-luxe py-32 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <Reveal className="lg:col-span-5 perspective-1000">
            <TiltCard maxTilt={5}>
              <div className="relative img-frame-3d">
                <img
                  src={MIRROR_CLIENT}
                  alt="Tamara com cliente"
                  className="w-full object-cover aspect-[4/5] glow-gold"
                />
                <div className="absolute -bottom-6 -right-6 lg:-right-12 bg-gold-bright text-ink p-6 max-w-[220px] shadow-[0_20px_60px_-10px_rgba(218,165,32,0.5)] float-3d">
                  <p className="font-serif italic text-2xl leading-tight">Lucia Piloto</p>
                  <p className="text-xs uppercase tracking-wider mt-1">Formação</p>
                </div>
              </div>
            </TiltCard>
          </Reveal>

          <Reveal className="lg:col-span-7">
            <p className="overline mb-4">— Sobre Tamara</p>
            <h2 className="font-serif text-5xl lg:text-6xl font-light text-ink leading-tight">
              Mais do que cabelo.<br />
              É <span className="italic gold-text">um ritual</span>.
            </h2>
            <p className="mt-6 text-ink/70 text-lg leading-relaxed">
              Tamara Alves dedica cada sessão a perceber o seu cabelo, o seu estilo de
              vida e o resultado que sonha. Com formação técnica avançada e um olhar
              estético apurado, é hoje uma referência em Balayage e morena iluminada na
              região de Mafra.
            </p>
            <p className="mt-4 text-ink/70 leading-relaxed">
              Trabalha exclusivamente com produtos profissionais Cotril — pelo cuidado, qualidade
              e durabilidade que merecem o seu cabelo.
            </p>
            <Link to="/sobre" data-testid="home-about-link" className="inline-flex items-center gap-2 mt-10 text-gold border-b border-gold/30 hover:border-gold pb-1 font-medium tracking-wide group">
              Conhecer a história <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ========== STUDIO PARALLAX ========== */}
      <section className="relative h-[70vh] overflow-hidden">
        <img
          src={SALON_INTERIOR_COTRIL}
          alt="Estúdio Tamara Alves Hairstylist"
          className="absolute inset-0 w-full h-full object-cover ken-burns"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
        <div className="absolute inset-0 flex items-end pb-20">
          <div className="container-luxe">
            <Reveal className="max-w-xl text-beige">
              <p className="overline !text-gold mb-4">— O Estúdio</p>
              <h2 className="font-serif text-5xl lg:text-6xl font-light leading-tight">
                Um espaço <span className="italic gold-text">só seu</span>.
              </h2>
              <p className="mt-4 text-beige/80 text-lg">
                Pequeno, exclusivo, com toda a gama profissional Cotril ao seu dispor.
              </p>
              <Link to="/contactos" className="inline-flex items-center gap-2 mt-8 text-gold border-b border-gold/40 pb-1 hover:border-gold font-medium tracking-wide group">
                <MapPin className="w-4 h-4" strokeWidth={1.5} />
                Venda do Pinheiro, Portugal
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ========== TESTIMONIALS ========== */}
      <section className="container-luxe py-24 sm:py-32" data-testid="home-testimonials">
        <Reveal className="text-center mb-16 max-w-2xl mx-auto">
          <p className="overline mb-4">— Testemunhos</p>
          <h2 className="font-serif text-5xl lg:text-6xl font-light text-ink leading-tight">
            O que dizem<br />
            as <span className="italic gold-text">clientes</span>.
          </h2>
        </Reveal>

        <Reveal stagger className="grid md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <TiltCard key={i} maxTilt={6} className="bg-beige-light p-10 border-t border-gold">
              <article data-testid={`testimonial-${i}`}>
                <Quote className="w-10 h-10 text-gold/30 mb-6" strokeWidth={1} />
                <p className="font-serif text-xl text-ink italic leading-relaxed mb-8">
                  "{t.text}"
                </p>
                <div className="flex justify-between items-end">
                  <p className="overline text-ink">{t.name}</p>
                  <div className="flex gap-0.5 text-gold">
                    {[...Array(t.rating)].map((_, j) => (
                      <Star key={j} className="w-3.5 h-3.5 fill-current" />
                    ))}
                  </div>
                </div>
              </article>
            </TiltCard>
          ))}
        </Reveal>
      </section>

      {/* ========== FEATURED PRODUCTS ========== */}
      {featuredProducts.length > 0 && (
        <section className="bg-beige-light border-y border-gold/10 py-24 sm:py-28" data-testid="home-featured-products">
          <div className="container-luxe">
            <Reveal className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
              <div>
                <p className="overline mb-3">— Pre-Shop Cotril</p>
                <h2 className="font-serif text-5xl lg:text-6xl font-light text-ink leading-tight">
                  Os meus <span className="italic gold-text">favoritos</span>.
                </h2>
                <p className="mt-4 text-ink/65 max-w-md">
                  Selecção pessoal da Tamara — os produtos que utilizo todos os dias.
                </p>
              </div>
              <Link to="/loja" className="inline-flex items-center gap-2 text-gold border-b border-gold/30 pb-1 hover:border-gold font-medium tracking-wide group whitespace-nowrap">
                Ver toda a loja <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>

            <Reveal stagger className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
              {featuredProducts.map((p) => (
                <TiltCard key={p.id} maxTilt={5} className="bg-white flex flex-col group">
                  <article data-testid={`home-product-${p.id}`} className="flex flex-col h-full">
                    <div className="aspect-square bg-beige overflow-hidden">
                      <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                    </div>
                    <div className="p-5 flex-1 flex flex-col">
                      <p className="overline mb-2">{p.line}</p>
                      <h3 className="font-serif text-lg text-ink leading-snug">{p.name}</h3>
                      <p className="text-xs text-ink/50">{p.size}</p>
                      <div className="mt-auto pt-4 flex items-end justify-between">
                        <span className="font-serif text-xl text-gold tabular-nums">{fmt(p.price)}</span>
                        <button
                          onClick={() => handleAdd(p)}
                          data-testid={`home-add-${p.id}`}
                          aria-label={`Adicionar ${p.name}`}
                          className="bg-ink text-beige w-9 h-9 flex items-center justify-center hover:bg-gold hover:text-ink transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </article>
                </TiltCard>
              ))}
            </Reveal>
          </div>
        </section>
      )}

      {/* ========== INSTAGRAM + GOOGLE ========== */}
      <section className="bg-ink text-beige py-24 sm:py-28 relative overflow-hidden">
        <span aria-hidden className="absolute -top-20 -right-20 font-serif italic text-[24rem] leading-none text-gold/[0.06] select-none pointer-events-none">@</span>

        <div className="container-luxe relative">
          <Reveal className="text-center mb-12">
            <p className="overline !text-gold mb-4">— Siga o meu trabalho</p>
            <h2 className="font-serif text-5xl lg:text-6xl font-light leading-tight">
              <span className="gold-text">@tamaraalveshairstylist</span>
            </h2>
            <p className="mt-5 max-w-xl mx-auto text-beige/70">
              Portfolio diário, transformações, behind-the-scenes e novidades.
            </p>
          </Reveal>

          {/* Gallery preview grid */}
          <Reveal stagger className="grid grid-cols-3 lg:grid-cols-6 gap-2 lg:gap-3 mb-12">
            {IG_PREVIEW.map((g) => (
              <a
                key={g.id}
                href="https://www.instagram.com/tamaraalveshairstylist/"
                target="_blank"
                rel="noreferrer"
                data-testid={`home-ig-tile-${g.id}`}
                className="relative aspect-square overflow-hidden group"
              >
                <img
                  src={g.src}
                  alt={g.alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/40 transition-colors duration-500 flex items-center justify-center">
                  <Instagram className="w-7 h-7 text-gold opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-[0_0_15px_rgba(255,215,0,0.8)]" strokeWidth={1.5} />
                </div>
              </a>
            ))}
          </Reveal>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://www.instagram.com/tamaraalveshairstylist/"
              target="_blank"
              rel="noreferrer"
              data-testid="home-instagram-link"
              className="btn-gold"
            >
              <Instagram className="w-4 h-4" /> Seguir no Instagram
            </a>
            <GoogleReviewButton variant="card" inverted />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
